import { Order } from "@amzn/ito-client";
import { Col, Flex, Row, Spacer, View } from "@amzn/stencil-react-components/layout";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { RowAttributesFunction, Table, TableSpacing } from "@amzn/stencil-react-components/table";
import { buildColumnDefinitions } from "common/helpers/table-utils";
import { ITOConsumers } from "common/types";
import { ColumnSetting } from "common/types/config-model";
import { PERMISSIONS } from "config";
import { UserPermissions } from "lib/services";
import React, { FC } from "react";

import { extractOrderData } from "./components/OrderDataExtractor";
import { HardwareDetailsSection, OrderDetailsSection, ShippingDetailsSection } from "./components/OrderDetailsSection";
import myOrdersTableConfig from "./my-hardware-orders.table-config.json";
import { useOrdersTable } from "./useOrdersPageTable";

export const MyHardwareOrdersTable: FC = () => {
  // Email Orders send the taxonomy as a hash
  const hash = window.location.hash.replace("#", "");

  const useHardwareOrderSupport = UserPermissions.current.hasPermission(PERMISSIONS.USE_SUPPORT_PHASE_2);

  const columns: ColumnSetting[] = myOrdersTableConfig.columns;

  const filteredColumns = columns.filter((column) => {
    if (column.id === "order_messages" && !useHardwareOrderSupport) {
      return false;
    }
    return true;
  });

  const currentColumnDefinitions = buildColumnDefinitions(filteredColumns);

  const { orders, isLoading } = useOrdersTable(ITOConsumers.AMZNHAM);

  /* istanbul ignore next */
  const expandableContent = ({ data, index }: { data: Order; index: number }) => {
    return buildExpandableContent(data, index);
  };

  /* eslint-disable @typescript-eslint/no-unused-vars */
  const getRowAttributes: RowAttributesFunction = ({ data }) => ({
    expandableContent,
    disabled: false,
    id: data.orderId,
    isDefaultExpanded: data.orderId === hash,
  });

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Col gridGap={32}>
      <Table
        actionHeader="Detail"
        columns={currentColumnDefinitions}
        data={orders}
        spacing={TableSpacing.Default}
        getRowAttributes={getRowAttributes}
      />
    </Col>
  );
};

const LoadingSpinner: FC = () => (
  <Flex flexDirection="column">
    <View alignSelf="center">
      <Spacer height={32} width={32} />
      <Spinner size={SpinnerSize.Medium} showText loadingText="Loading your orders" />
    </View>
  </Flex>
);

export const buildExpandableContent = (order: Order, index: number): React.JSX.Element[] => {
  const innerContent: React.JSX.Element[] = [];
  const orderData = extractOrderData(order);

  innerContent.push(
    <Row key={`detailsRow_${index}`} gridGap="S400" style={{ width: "100%" }}>
      <OrderDetailsSection index={index} orderData={orderData} />
      <div style={{ width: "1px", backgroundColor: "#e0e0e0", margin: "0 8px" }} />
      <ShippingDetailsSection index={index} orderData={orderData} />
    </Row>
  );

  if (order.status === "DELIVERED") {
    innerContent.push(
      <Row key={`hardwareRow_${index}`} gridGap="S400" style={{ width: "100%", paddingTop: "8px" }}>
        <HardwareDetailsSection index={index} orderData={orderData} />
      </Row>
    );
  }

  return innerContent;
};

export default MyHardwareOrdersTable;
